import { readMpgdPreferredLocales, type MpgdLocale } from '@mpgd/i18n';

/**
 * Game UI languages, in settings-menu order. Kit's `@mpgd/i18n` only carries ko/en, so the game owns
 * this wider set. Korean and English are authored inline next to each table; the rest live in
 * `./translations`. Puzzle glyphs (넵/넴) and user-authored titles are never translated.
 */
export const gameLocales = [
  'ko',
  'en',
  'ja',
  'zh-Hans',
  'zh-Hant',
  'es',
  'pt-BR',
  'fr',
  'de',
  'id',
  'vi',
  'th',
] as const;
export type Locale = (typeof gameLocales)[number];
export type TranslatedLocale = Exclude<Locale, 'ko' | 'en'>;

/** Language names are always shown in their own language so a player can find theirs. */
export const languageNames: Readonly<Record<Locale, string>> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  es: 'Español',
  'pt-BR': 'Português (Brasil)',
  fr: 'Français',
  de: 'Deutsch',
  id: 'Bahasa Indonesia',
  vi: 'Tiếng Việt',
  th: 'ไทย',
};

export function isGameLocale(input: unknown): input is Locale {
  return typeof input === 'string' && (gameLocales as readonly string[]).includes(input);
}

const traditionalChineseRegions = new Set(['tw', 'hk', 'mo']);

/** Maps a BCP 47 tag (`zh-TW`, `pt_PT`, `in-ID`) to the closest game locale, or undefined. */
export function normalizeGameLocale(input: string): Locale | undefined {
  const [base = '', ...rest] = input.trim().toLowerCase().split(/[-_]/);
  if (base === 'zh') {
    return rest.includes('hant') || (!rest.includes('hans') && rest.some((part) => traditionalChineseRegions.has(part)))
      ? 'zh-Hant'
      : 'zh-Hans';
  }
  if (base === 'pt') {
    return 'pt-BR';
  }
  // Older Android and Java runtimes still report Indonesian as `in`.
  if (base === 'in') {
    return 'id';
  }
  return gameLocales.find((locale) => locale === base);
}

/** Kit surfaces only localize ko/en; every other game locale reads Kit copy in English. */
export function kitLocale(locale: Locale): MpgdLocale {
  return locale === 'ko' ? 'ko' : 'en';
}

/** Same policy as Kit's `resolveTargetMpgdLocale`, over the game's own locale set. */
export function resolveTargetGameLocale(input: {
  readonly localizedContent: boolean;
  readonly savedLocale?: Locale | undefined;
  readonly preferredLocales?: readonly string[];
  readonly fallbackLocale: Locale;
}): Locale {
  if (!input.localizedContent) {
    return input.fallbackLocale;
  }
  if (input.savedLocale !== undefined) {
    return input.savedLocale;
  }
  for (const preferred of input.preferredLocales ?? readMpgdPreferredLocales()) {
    const locale = normalizeGameLocale(preferred);
    if (locale !== undefined) {
      return locale;
    }
  }
  return input.fallbackLocale;
}

/** `{name}` substitution shared by every table; unknown names stay visible for tests to catch. */
export function format(template: string, values: Readonly<Record<string, string | number>> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) =>
    String(values[name] ?? match),
  );
}
